import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../lib/i18n/LanguageContext';

interface WineListLoadingErrorProps {
  isLoading: boolean;
  error: string | null;
}

const WineListLoadingError = ({ isLoading, error }: WineListLoadingErrorProps) => {
  const { t } = useLanguage();

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="pt-24 pb-20 bg-neutral-50"
    >
      <Helmet>
        <title>{t.wineList.hero.title} | Rossopomodoro</title>
        <meta
          name="description"
          content={`${t.wineList.hero.title} - ${t.wineList.hero.subtitle}. ${t.wineList.description.substring(0, 150)}...`}
        />
        <link rel="canonical" href="https://rossopomodoro.is/wine-list" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rossopomodoro.is/wine-list" />
        <meta property="og:title" content={`${t.wineList.hero.title} | Rossopomodoro Reykjavik`} />
        <meta property="og:description" content={t.wineList.hero.subtitle} />
        <meta property="og:image" content="https://rossopomodoro.is/hero11.jpg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://rossopomodoro.is/wine-list" />
        <meta
          property="twitter:title"
          content={`${t.wineList.hero.title} | Rossopomodoro Reykjavik`}
        />
        <meta property="twitter:description" content={t.wineList.hero.subtitle} />
        <meta property="twitter:image" content="https://rossopomodoro.is/hero11.jpg" />
      </Helmet>
      <div className="container-custom">
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-red-800" />
            <span className="ml-2 text-neutral-600">Loading wine list...</span>
          </div>
        ) : (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <p className="text-neutral-600 mb-4">Failed to load wine list</p>
              <p className="text-sm text-neutral-500">{error}</p>
            </div>
          </div>
        )}
      </div>
    </motion.section>
  );
};

export default WineListLoadingError;
